import React, { useContext } from 'react';
import { FeatureDebugger, DiagnosticStep } from './FeatureDebugger';
import { AuthContext } from '../../contexts/AuthContext';

interface AuthDebuggerProps {
  children?: React.ReactNode;
  autoRun?: boolean;
}

export const AuthDebugger: React.FC<AuthDebuggerProps> = ({ children, autoRun }) => {
  // Try to get auth context, but don't fail if it doesn't exist
  const authContext = useContext(AuthContext);
  const token = authContext?.token || null;
  const user = authContext?.user || null;
  const isAuthenticated = authContext?.isAuthenticated || false;

  const authSteps: DiagnosticStep[] = [
    {
      id: 'browser-storage',
      label: 'Browser storage accessible',
      critical: true,
      check: async () => {
        try {
          const testKey = 'auth-debug-test';
          localStorage.setItem(testKey, 'test');
          const value = localStorage.getItem(testKey);
          localStorage.removeItem(testKey);

          return {
            passed: value === 'test',
            details: {
              localStorageAvailable: true,
              sessionStorageAvailable: typeof sessionStorage !== 'undefined'
            }
          };
        } catch (error) {
          return {
            passed: false,
            error: 'localStorage not available (private mode?)',
            details: { error: error instanceof Error ? error.message : 'Unknown' }
          };
        }
      }
    },
    {
      id: 'token-presence',
      label: 'Auth token exists',
      critical: true,
      check: async () => {
        const storedToken = localStorage.getItem('authToken');
        const contextToken = token;

        return {
          passed: !!(storedToken || contextToken),
          details: {
            hasStoredToken: !!storedToken,
            hasContextToken: !!contextToken,
            tokensMatch: storedToken === contextToken,
            tokenLength: storedToken?.length || 0
          }
        };
      }
    },
    {
      id: 'token-format',
      label: 'Token format valid (JWT structure)',
      critical: true,
      check: async () => {
        const currentToken = token || localStorage.getItem('authToken');
        if (!currentToken) {
          return {
            passed: false,
            error: 'No token to validate'
          };
        }

        const parts = currentToken.split('.');
        const hasThreeParts = parts.length === 3;

        try {
          if (hasThreeParts) {
            const header = JSON.parse(atob(parts[0].replace(/-/g, '+').replace(/_/g, '/')));
            const payload = JSON.parse(atob(parts[1].replace(/-/g, '+').replace(/_/g, '/')));

            return {
              passed: true,
              details: {
                header,
                payload: {
                  ...payload,
                  exp: payload.exp ? new Date(payload.exp).toISOString() : undefined,
                  iat: payload.iat ? new Date(payload.iat).toISOString() : undefined
                }
              }
            };
          }
        } catch (error) {
          return {
            passed: false,
            error: 'Invalid JWT format - cannot decode',
            details: { parts: parts.length }
          };
        }

        return {
          passed: false,
          error: `Invalid JWT structure: ${parts.length} parts (expected 3)`
        };
      }
    },
    {
      id: 'token-expiry',
      label: 'Token not expired',
      critical: false,
      check: async () => {
        const currentToken = token || localStorage.getItem('authToken');
        if (!currentToken) {
          return {
            passed: false,
            error: 'No token to check'
          };
        }

        try {
          const parts = currentToken.split('.');
          const payload = JSON.parse(atob(parts[1].replace(/-/g, '+').replace(/_/g, '/')));

          const now = Date.now();
          const exp = payload.exp;
          const timeLeft = exp - now;
          const isExpired = timeLeft <= 0;

          return {
            passed: !isExpired,
            details: {
              expiresAt: new Date(exp).toISOString(),
              currentTime: new Date(now).toISOString(),
              timeLeftMs: timeLeft,
              timeLeftMinutes: Math.floor(timeLeft / 1000 / 60),
              isExpired
            },
            error: isExpired ? `Token expired ${Math.abs(Math.floor(timeLeft / 1000 / 60))} minutes ago` : undefined
          };
        } catch (error) {
          return {
            passed: false,
            error: 'Cannot parse token expiry',
            details: { error: error instanceof Error ? error.message : 'Unknown' }
          };
        }
      }
    },
    {
      id: 'user-data',
      label: 'User data synchronized',
      check: async () => {
        const storedUser = localStorage.getItem('authUser');
        const contextUser = user;

        let parsedUser = null;
        if (storedUser) {
          try {
            parsedUser = JSON.parse(storedUser);
          } catch {
            return {
              passed: false,
              error: 'Corrupted user data in localStorage'
            };
          }
        }

        return {
          passed: !!(parsedUser || contextUser),
          details: {
            hasStoredUser: !!parsedUser,
            hasContextUser: !!contextUser,
            userId: parsedUser?.id || contextUser?.id,
            userEmail: parsedUser?.email || contextUser?.email,
            dataInSync: JSON.stringify(parsedUser) === JSON.stringify(contextUser)
          }
        };
      }
    },
    {
      id: 'auth-context',
      label: 'Auth context state valid',
      check: async () => {
        return {
          passed: (!!token && !!user && isAuthenticated) || (!token && !user && !isAuthenticated),
          details: {
            hasToken: !!token,
            hasUser: !!user,
            isAuthenticated,
            stateConsistent: (!!token === !!user) && (!!token === isAuthenticated)
          },
          error: (!!token !== isAuthenticated) ? 'Token/auth state mismatch' : undefined
        };
      }
    },
    {
      id: 'api-connectivity',
      label: 'API endpoint reachable',
      check: async () => {
        const startTime = performance.now();
        try {
          const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:8787';
          const response = await fetch(`${apiUrl}/health`, {
            method: 'GET',
            signal: AbortSignal.timeout(5000)
          });

          const responseTime = performance.now() - startTime;

          return {
            passed: response.ok,
            details: {
              status: response.status,
              statusText: response.statusText,
              responseTimeMs: responseTime,
              apiUrl
            }
          };
        } catch (error) {
          return {
            passed: false,
            error: error instanceof Error ? error.message : 'Cannot reach API',
            details: {
              responseTimeMs: performance.now() - startTime,
              errorType: error instanceof Error ? error.name : 'Unknown'
            }
          };
        }
      }
    },
    {
      id: 'token-verification',
      label: 'Token validates with backend',
      check: async () => {
        const currentToken = token || localStorage.getItem('authToken');
        if (!currentToken) {
          return {
            passed: false,
            error: 'No token to verify'
          };
        }

        try {
          const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:8787';
          const response = await fetch(`${apiUrl}/verify-token`, {
            method: 'GET',
            headers: {
              'Authorization': `Bearer ${currentToken}`
            },
            signal: AbortSignal.timeout(5000)
          });

          const isValid = response.ok;
          let userData = null;

          if (isValid) {
            try {
              userData = await response.json();
            } catch {
              // Response might not be JSON
            }
          }

          return {
            passed: isValid,
            details: {
              status: response.status,
              validToken: isValid,
              userData
            },
            error: !isValid ? `Backend rejected token (${response.status})` : undefined
          };
        } catch (error) {
          return {
            passed: false,
            error: 'Cannot verify token with backend',
            details: {
              error: error instanceof Error ? error.message : 'Unknown'
            }
          };
        }
      }
    },
    {
      id: 'cors-headers',
      label: 'CORS properly configured',
      check: async () => {
        try {
          const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:8787';
          const response = await fetch(`${apiUrl}/health`, {
            method: 'OPTIONS',
            headers: {
              'Origin': window.location.origin,
              'Access-Control-Request-Method': 'GET',
              'Access-Control-Request-Headers': 'Authorization'
            },
            signal: AbortSignal.timeout(5000)
          });

          const allowOrigin = response.headers.get('Access-Control-Allow-Origin');
          const allowMethods = response.headers.get('Access-Control-Allow-Methods');
          const allowHeaders = response.headers.get('Access-Control-Allow-Headers');

          const originAllowed = allowOrigin === '*' || allowOrigin === window.location.origin;
          const methodsIncludeGet = allowMethods?.includes('GET') || false;
          const headersIncludeAuth = allowHeaders?.toLowerCase().includes('authorization') || false;

          return {
            passed: originAllowed && methodsIncludeGet && headersIncludeAuth,
            details: {
              currentOrigin: window.location.origin,
              allowedOrigin: allowOrigin,
              allowedMethods: allowMethods,
              allowedHeaders: allowHeaders,
              checksPass: {
                origin: originAllowed,
                methods: methodsIncludeGet,
                headers: headersIncludeAuth
              }
            }
          };
        } catch (error) {
          return {
            passed: false,
            error: 'CORS preflight failed',
            details: {
              error: error instanceof Error ? error.message : 'Unknown'
            }
          };
        }
      }
    },
    {
      id: 'network-quality',
      label: 'Network connection stable',
      check: async () => {
        const navigator = window.navigator as any;
        const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;

        return {
          passed: navigator.onLine,
          details: {
            online: navigator.onLine,
            effectiveType: connection?.effectiveType,
            downlink: connection?.downlink,
            rtt: connection?.rtt,
            saveData: connection?.saveData
          },
          error: !navigator.onLine ? 'Browser reports offline' : undefined
        };
      }
    }
  ];

  return (
    <FeatureDebugger
      title="Authentication System"
      steps={authSteps}
      autoRun={autoRun}
    >
      {children}
    </FeatureDebugger>
  );
};